// FreeAuditPage.js — public self-serve 3-call audit uploader.
// Replaces the mailto: CTAs across /pricing, /vs-dialpad, /vs-aircall,
// /vs-ringcx, /vs-gong and /why-voicefabric. Public — no login required.
// Server-side guardrails: honeypot, ≤3 files, ≤25 MB/file, IP rate-limit.
import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, Upload, X, ArrowRight, CheckCircle2, ShieldCheck, Loader2, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MAX_FILES = 3;
const MAX_MB = 25;
const ROLES = [
  { value: 'sales', label: 'Sales — outbound / discovery / close' },
  { value: 'support', label: 'Support — customer service / helpdesk' },
  { value: 'admin', label: 'Admin — reception / bookings / general' },
  { value: 'accounts', label: 'Accounts — collections / payment queries' },
  { value: 'management', label: 'Management — coaching / 1:1s' },
  { value: 'mixed', label: 'Mixed — a bit of everything' },
];

const FreeAuditPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [role, setRole] = useState('sales');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState(''); // honeypot — hidden
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // {id, file_count}
  const dropRef = useRef(null);

  const addFiles = (incoming) => {
    setError('');
    const list = Array.from(incoming || []);
    const next = [...files];
    for (const f of list) {
      if (next.length >= MAX_FILES) {
        setError(`Maximum ${MAX_FILES} files per submission.`);
        break;
      }
      if (f.size > MAX_MB * 1024 * 1024) {
        setError(`"${f.name}" is larger than ${MAX_MB} MB.`);
        continue;
      }
      next.push(f);
    }
    setFiles(next);
  };

  const removeFile = (idx) => {
    setFiles(files.filter((_, i) => i !== idx));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (files.length === 0) {
      setError('Please attach at least one audio file (up to 3).');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('name', name);
      fd.append('email', email);
      fd.append('company', company);
      fd.append('role', role);
      fd.append('phone', phone);
      fd.append('website', website); // honeypot
      files.forEach((f) => fd.append('files', f, f.name));
      const r = await axios.post(`${API}/audit-intake/submit`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDone({ id: r.data.id, fileCount: r.data.file_count });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Something went wrong. Please try again.';
      setError(typeof msg === 'string' ? msg : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C]" data-testid="free-audit-page">
      {/* Header */}
      <header className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-teal-500 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="text-white font-bold">Yabbit VoiceFabric</span>
          </Link>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/why-voicefabric" className="text-white/60 hover:text-white">Why VoiceFabric</Link>
            <Link to="/pricing" className="text-white/60 hover:text-white">Pricing</Link>
          </div>
        </div>
      </header>

      {done ? (
        // ── Success screen ─────────────────────────────────────────
        <section className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="free-audit-success">
          <div className="w-16 h-16 rounded-full bg-teal-500/15 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-8 h-8 text-teal-400" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-white leading-tight mb-4">
            Thanks — your audit is queued.
          </h1>
          <p className="text-white/70 text-lg leading-relaxed">
            We&apos;ve received your <strong className="text-white">{done.fileCount} call{done.fileCount === 1 ? '' : 's'}</strong>. Our team will
            transcribe them, generate a top-line summary + sentiment for each, and reply to
            <span className="text-teal-300 font-semibold"> {email}</span> within the next few business hours with the results and a coaching preview.
          </p>
          <p className="text-white/40 text-xs mt-6 font-mono">Reference: {done.id}</p>
          <div className="mt-10 flex items-center justify-center gap-3 flex-wrap">
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
              data-testid="free-audit-success-pricing"
            >
              See pricing
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/why-voicefabric"
              className="inline-flex items-center gap-2 text-teal-300 hover:text-teal-200 font-semibold px-4 py-3"
            >
              Why VoiceFabric →
            </Link>
          </div>
        </section>
      ) : (
        <>
          {/* Hero */}
          <section className="max-w-3xl mx-auto px-6 pt-16 pb-8 text-center">
            <p className="text-teal-300 text-xs font-semibold uppercase tracking-wider mb-3">Free 3-call audit</p>
            <h1 className="text-4xl sm:text-5xl font-black text-white leading-tight tracking-tight">
              Send us three calls.<br /><span className="text-teal-400">We&apos;ll grade them free.</span>
            </h1>
            <p className="text-white/70 text-lg mt-5 max-w-xl mx-auto leading-relaxed">
              Upload up to three of your own recordings. Within a few business hours you&apos;ll receive a transcript, a
              top-line sentiment score, and the single coaching focus that would move the needle most on each call. No credit card, no sales pressure.
            </p>
          </section>

          {/* Form */}
          <section className="max-w-2xl mx-auto px-6 py-10">
            <form onSubmit={onSubmit} className="bg-white/5 border border-white/10 rounded-2xl p-8 space-y-6" data-testid="free-audit-form">
              {/* Contact fields */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/70 text-xs font-semibold uppercase tracking-wider mb-2">Your name</label>
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-teal-500 transition-colors"
                    placeholder="Sarah Johnson"
                    data-testid="free-audit-name"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-xs font-semibold uppercase tracking-wider mb-2">Work email</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-teal-500 transition-colors"
                    placeholder="sarah@yourcompany.com.au"
                    data-testid="free-audit-email"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-xs font-semibold uppercase tracking-wider mb-2">Company</label>
                  <input
                    type="text"
                    required
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-teal-500 transition-colors"
                    placeholder="Your business name"
                    data-testid="free-audit-company"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-xs font-semibold uppercase tracking-wider mb-2">Phone <span className="text-white/40 normal-case">(optional)</span></label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-teal-500 transition-colors"
                    placeholder="+61 4xx xxx xxx"
                    data-testid="free-audit-phone"
                  />
                </div>
              </div>

              {/* Role */}
              <div>
                <label className="block text-white/70 text-xs font-semibold uppercase tracking-wider mb-2">These calls are mostly…</label>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-teal-500 transition-colors"
                  data-testid="free-audit-role"
                >
                  {ROLES.map((r) => (
                    <option key={r.value} value={r.value} className="bg-[#0A0F1C]">
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* File dropzone */}
              <div>
                <label className="block text-white/70 text-xs font-semibold uppercase tracking-wider mb-2">
                  Audio files <span className="text-white/40 normal-case">(up to {MAX_FILES}, max {MAX_MB} MB each)</span>
                </label>
                <div
                  ref={dropRef}
                  onDragOver={(e) => { e.preventDefault(); dropRef.current?.classList.add('border-teal-500', 'bg-teal-500/5'); }}
                  onDragLeave={() => dropRef.current?.classList.remove('border-teal-500', 'bg-teal-500/5')}
                  onDrop={(e) => {
                    e.preventDefault();
                    dropRef.current?.classList.remove('border-teal-500', 'bg-teal-500/5');
                    addFiles(e.dataTransfer.files);
                  }}
                  className="border-2 border-dashed border-white/15 rounded-xl p-8 text-center transition-colors cursor-pointer hover:border-teal-500/50"
                  onClick={() => document.getElementById('free-audit-file-input')?.click()}
                  data-testid="free-audit-dropzone"
                >
                  <Upload className="w-8 h-8 text-white/40 mx-auto mb-3" />
                  <p className="text-white/70 text-sm">
                    <span className="text-teal-300 font-semibold">Click to browse</span> or drag your call recordings here
                  </p>
                  <p className="text-white/40 text-xs mt-1">mp3 · m4a · wav · webm · flac · ogg · aac</p>
                  <input
                    id="free-audit-file-input"
                    type="file"
                    accept="audio/*,.mp3,.m4a,.wav,.webm,.flac,.ogg,.aac,.mp4,.mpga,.mpeg"
                    multiple
                    className="hidden"
                    onChange={(e) => addFiles(e.target.files)}
                    data-testid="free-audit-file-input"
                  />
                </div>

                {files.length > 0 && (
                  <ul className="mt-4 space-y-2" data-testid="free-audit-file-list">
                    {files.map((f, i) => (
                      <li key={i} className="flex items-center justify-between bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm">
                        <div className="min-w-0 flex-1">
                          <div className="text-white truncate">{f.name}</div>
                          <div className="text-white/40 text-xs">{(f.size / 1024 / 1024).toFixed(1)} MB</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(i)}
                          className="ml-3 p-1 rounded hover:bg-white/10 text-white/50 hover:text-white transition-colors"
                          aria-label="Remove file"
                          data-testid={`free-audit-remove-${i}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Honeypot — hidden from humans but bots will fill it */}
              <input
                type="text"
                name="website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="hidden"
              />

              {error && (
                <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-sm text-red-200" data-testid="free-audit-error">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-400 disabled:bg-white/10 disabled:text-white/40 disabled:cursor-not-allowed text-white font-bold px-6 py-4 rounded-lg text-base transition-colors"
                  data-testid="free-audit-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Uploading…
                    </>
                  ) : (
                    <>
                      Get my free audit
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
                <p className="text-white/40 text-xs mt-3 text-center flex items-center justify-center gap-1.5">
                  <ShieldCheck className="w-3 h-3" />
                  Audio is deleted after transcription. We only keep the text.
                </p>
              </div>
            </form>
          </section>
        </>
      )}

      <footer className="border-t border-white/5 py-8 text-center text-white/30 text-xs mt-10">
        © 2026 Yabbit VoiceFabric · yabbit.com.au · <Link to="/pricing" className="hover:text-white/50">Pricing</Link> · <Link to="/why-voicefabric" className="hover:text-white/50">Why VoiceFabric</Link>
      </footer>
    </div>
  );
};

export default FreeAuditPage;
